import { useInkind } from '@gik/api/inkinds/inkind';
import { useIsMounted } from '@gik/core/hooks/hooks/useIsMounted';
import routes from '@gik/core/routes.ts';
import { useInkindCan } from '@gik/core/store/permissions';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { navigateTo } from '@gik/core/utils/navigateTo';
import { getPageSettingsFeatureHref } from '@gik/inkind-page/components/InkindPageSettingsModal/utils/PageSettingsScrollNavBar';
import { Heading, IconButtonCircle, Layout, UI } from '@gik/ui';
import { SidebarSection } from '@gik/ui-legacy/gik/SidebarSection';
import React from 'react';

type WishlistSidebarSectionProps = {
  inkindRouteId: string;
  disabled?: boolean;
  isFullWishlistPage?: boolean;
} & UIComponent;

export function WishlistSidebarSection({
  inkindRouteId,
  className,
  children,
  disabled,
  isFullWishlistPage,
  ...otherProps
}: React.PropsWithChildren<WishlistSidebarSectionProps>) {
  const bem = useBemCN('wishlist-sidebar-section');

  const { data: inkindPage } = useInkind(inkindRouteId);
  const giftingUrl = inkindPage?.inkindSituations?.length > 0 && inkindPage.inkindSituations[0]?.giftingUrl;

  const canEdit = useInkindCan('manage', inkindRouteId, inkindPage?.groupId);
  const mounted = useIsMounted();

  const giftPickerUrl = React.useMemo(() => {
    return isFullWishlistPage ? giftingUrl : routes.inkinds + '/' + inkindRouteId + '/' + '/gift-picker';
  }, [isFullWishlistPage]);

  // `canEdit` is false during SSR (permissions/inkind data resolve on the
  // client) so both buttons must also be gated behind `mounted()` — otherwise
  // the client renders a <button> the server did not, causing a hydration
  // mismatch. See docs/guides/hydration-troubleshooting.md.
  const isMounted = mounted();
  const shouldShowEditButton = canEdit && isMounted;
  const shouldShowAddButton = !disabled && canEdit && isMounted;

  return (
    <SidebarSection
      {...otherProps}
      {...bem(null, null, ['mt-4', className])}
      title={
        <Layout horizontal>
          <Heading as="span">Wishlist</Heading>

          {shouldShowEditButton && (
            <IconButtonCircle
              onClick={() => navigateTo(getPageSettingsFeatureHref(inkindRouteId, 'wishlist'))}
              size={UI.Size.LG}
              color={UI.Color.White}
              elevation={UI.Elevation.SM}
              iconColor={UI.Color.Secondary}
              iconSize={'18px'}
              name="settings"
            />
          )}
        </Layout>
      }
      append={
        shouldShowAddButton && (
          <IconButtonCircle
            onClick={async event => {
              return true;
            }}
            href={giftPickerUrl || '#'}
            as="a"
            iconColor={UI.Color.White}
            size={UI.Size.XL}
            name="plus"
          />
        )
      }
    >
      {children}
    </SidebarSection>
  );
}
